'use client';

import { useGetFeatureFlagVariant } from '@/lib/feature-flags/hooks/use-get-feature-flag-variant';
import type { FeatureFlagKeys, FeatureFlagVariant } from '@/lib/feature-flags/types';

/**
 * ONE ARM PER USER, ACROSS EVERY EXTERN LABS VERTICAL.
 *
 * THE BUG THIS FIXES. Arms were assigned per VERTICAL: each vertical read its own flag, so the same
 * person could be `control` on finance and `experiment` on data. Every trialer resolves eligibility for
 * BOTH verticals, so control groups were being treated by the other vertical's offer. Measured
 * 2026-08-01: **20% of finance-control users were shown a data Labs offer within 7 days (27/134), and
 * 11% of data-control users were shown a finance offer (14/125).**
 *
 * A control group that receives the treatment biases every causal read TOWARD ZERO. It never inflates
 * an effect, so the true impact of Labs is larger than what the board has been reporting, never
 * smaller. The contamination stayed at 11-20% rather than ~75% only because of the reach problem: most
 * experiment-arm learners were never shown anything at all.
 *
 * THE FIX. One flag decides the arm for the whole product. A learner is either in the Labs treatment or
 * in the holdout, everywhere, on every vertical and on both sides of the paywall. Each vertical's own
 * flag stays in place to say whether that VERTICAL is live, but it no longer decides who is treated.
 *
 * 2026-08 MIGRATION: THE CANONICAL FLAG IS NOW `labs_intent_overlay_q3_2026`, not finance's key.
 * Finance was canonical while its cohort was the one worth preserving (longest history, largest
 * sample, the only read near significance). That cohort is now closed: the doors experiment ran Aug 5
 * to Aug 20 and read flat on both primaries, so there is nothing left to preserve and every learner
 * in it carries a weeks-old assignment. A brand-new key re-randomises the whole population, which is
 * exactly what the intent-gated overlay experiment needs — its cohort starts at first eligibility
 * AFTER this ships. Window every read to that boundary; do not pool across it.
 *
 * ⚠️ THIS CHANGES WHO IS IN WHICH ARM, so it resets comparability the same way the 25/75 → 50/50
 * widening did on 2026-07-31. Reads must be windowed to first eligibility AFTER this ships, and it
 * needs eng sign-off before merge. Analytics keep BOTH stamps (`arm` = the canonical arm that actually
 * decided treatment; `vertical_arm` = the vertical's own flag) so historical series stay
 * interpretable and the migration is auditable rather than silent.
 */

/**
 * The single flag that decides Labs treatment for a learner, on every vertical.
 *
 * Do NOT read a pack's own `featureFlag` to decide whether to show an offer. That is what caused the
 * cross-contamination above. Use this.
 */
export const LABS_ARM_FLAG: FeatureFlagKeys = 'labs_intent_overlay_q3_2026';

/**
 * Operational kill switch over the two doors added in PRO-369 (checkout-success and pre-paywall).
 *
 * NOT AN ARM, and deliberately not this file's other export. It never decides who is in the experiment
 * and it must never be consulted before firing an ITT event. Its only job is to let the doors be
 * withdrawn on their own if they dent paid conversion, without reverting a merged PR or turning off
 * `LABS_ARM_FLAG` and collapsing the holdout we are trying to read.
 *
 * Read it with `useGetFeatureFlagKillSwitch`, which fails OPEN. The doors are shipped surfaces; an
 * unresolved flag must not silently withdraw them.
 */
export const LABS_DOORS_FLAG: FeatureFlagKeys = 'ff_labs_doors';

export interface LabsArm {
  /** The learner's Labs arm, or undefined until the flag resolves. */
  arm: FeatureFlagVariant | undefined;
  /** True once the flag has resolved to a real arm. */
  isReady: boolean;
  /** True only when the learner is in the treatment arm. The holdout is everything else. */
  isTreated: boolean;
}

export function useLabsArm(): LabsArm {
  const { variant, isReady } = useGetFeatureFlagVariant(LABS_ARM_FLAG);

  return {
    arm: variant,
    isReady,
    // Dark by default: an unresolved flag is NOT treated, so a learner never sees an offer stamped
    // with an arm we could not determine.
    isTreated: isReady && variant === 'experiment',
  };
}
